import React, { useMemo, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import AddSalesChallanForm from './AddSalesChallanForm';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import AirDatePicker from '../components/ui/AirDatePicker';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';

function formatDateDDMMYYYY(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${dd}-${mm}-${yyyy}`;
}

function formatDateTimeDDMMYYYY(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy}-${hh}:${min}:${ss}`;
}

const getInvoiceDateTime = (r) => {
  if (r.created_at) return formatDateTimeDDMMYYYY(r.created_at);
  if (r.createdAt) return formatDateTimeDDMMYYYY(r.createdAt);
  return formatDateTimeDDMMYYYY(r.invoiceDate || r.challan_date);
};

function SalesChallanList({ onPreview, onAddInvoice, onEditInvoice, onConvert, onNavigateToProfile, isReport = false }) {
  const { hasPermission } = usePermissionContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const [draftCustomer, setDraftCustomer] = useState('');
  const [appliedCustomer, setAppliedCustomer] = useState('');

  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  const [exportType, setExportType] = useState('');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportColumns, setExportColumns] = useState({
    customerName: true,
    customerTradeName: true,
    invoiceNo: true,
    invoiceDate: true,
    totalAmount: true,
    saleType: true
  });

  const columnLabels = {
    customerName: 'Customer Name',
    customerTradeName: 'Trade Name',
    invoiceNo: 'Challan No',
    invoiceDate: 'Challan Date',
    totalAmount: 'Total Amount',
    saleType: 'Sale Type'
  };

  const user = JSON.parse(sessionStorage.getItem('user'));
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const totalCols = 7 + (isSuperAdmin ? 1 : 0) + (hasPermission('Sales Challan', 'view') ? 1 : 0) + ((!isReport && (hasPermission('Sales Challan', 'update') || hasPermission('Sales Challan', 'delete'))) ? 1 : 0);

  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'descending' });
  const [customers, setCustomers] = useState([]);

  const toggleExportColumn = (key) => {
    setExportColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const result = await fetchApi('/customer?limit=1000');
      setCustomers(result.customers || result || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchChallans = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (search) queryParams.append('search', search);
      queryParams.append('page', page);
      queryParams.append('limit', pageSize);
      if (appliedCustomer) queryParams.append('customer', appliedCustomer);
      if (appliedStartDate) queryParams.append('startDate', appliedStartDate);
      if (appliedEndDate) queryParams.append('endDate', appliedEndDate);
      queryParams.append('status', 'Pending');
      const result = await fetchApi(`/sales-challan?${queryParams.toString()}`);
      setRows(result.salesChallans || []);
      setTotalEntries(result.total || 0);
      setTotalPages(result.pages || 1);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch challans");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchChallans();
    fetchHistory();
  }, [page, pageSize, appliedCustomer, appliedStartDate, appliedEndDate, search, isReport]);

  const fetchHistory = async () => {
    try {
      const data = await fetchApi('/sales-challan?limit=20&status=Converted');
      setHistory(data.salesChallans || []);
    } catch (err) {
      console.error("History fetch error:", err);
    }
  };

  const [selectedIdx, setSelectedIdx] = useState(0);

  const handleCustomerClick = (r) => {
    const fullCust = customers.find(c => (c.id || c._id) === r.customer_id);
    if (onNavigateToProfile) {
      onNavigateToProfile(fullCust || {
        id: r.customer_id,
        _id: r.customer_id,
        customerName: r.customerName,
        customerTradeName: r.customerTradeName
      });
    }
  };

  const sortedRows = useMemo(() => {
    let sortableItems = [...rows];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'totalAmount') {
          aValue = parseFloat(aValue || 0);
          bValue = parseFloat(bValue || 0);
        } else if (sortConfig.key === 'invoiceDate') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [rows, sortConfig]);

  // Keyboard navigation: Up, Down, Enter
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (sortedRows.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx((prev) => (prev + 1) % sortedRows.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((prev) => (prev - 1 + sortedRows.length) % sortedRows.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selectedItem = sortedRows[selectedIdx];
        if (selectedItem) {
          handleCustomerClick(selectedItem);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sortedRows, selectedIdx]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const onSubmitFilters = () => {
    setAppliedCustomer(draftCustomer);
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
    setPage(1);
  };

  const onResetFilters = () => {
    setDraftCustomer('');
    setDraftStartDate('');
    setDraftEndDate('');
    setAppliedCustomer('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setSearch('');
    setPage(1);
  };

  const handleExcelDownload = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (search) queryParams.append('search', search);
      if (appliedCustomer) queryParams.append('customer', appliedCustomer);
      if (appliedStartDate) queryParams.append('startDate', appliedStartDate);
      if (appliedEndDate) queryParams.append('endDate', appliedEndDate);
      queryParams.append('limit', '0');
      queryParams.append('status', 'Pending');

      const result = await fetchApi(`/sales-challan?${queryParams.toString()}`);
      const challansToExport = result.salesChallans || [];

      const worksheetData = challansToExport.map((r, idx) => {
        const rowData = { 'No.': idx + 1 };
        Object.entries(columnLabels).forEach(([key, label]) => {
          if (exportColumns[key]) {
            if (key === 'invoiceDate') {
              rowData[label] = formatDateDDMMYYYY(r.invoiceDate);
            } else {
              rowData[label] = r[key] || '';
            }
          }
        });
        return rowData;
      });

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'SalesChallans');
      XLSX.writeFile(workbook, 'SalesChallanList.xlsx');
    } catch (error) {
      console.error("Excel Download Error:", error);
      toast.error("Failed to download Excel");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (search) queryParams.append('search', search);
      if (appliedCustomer) queryParams.append('customer', appliedCustomer);
      if (appliedStartDate) queryParams.append('startDate', appliedStartDate);
      if (appliedEndDate) queryParams.append('endDate', appliedEndDate);
      queryParams.append('limit', '0'); // Fetch all for printing
      queryParams.append('status', 'Pending');

      const result = await fetchApi(`/sales-challan?${queryParams.toString()}`);
      let challansToPrint = result.salesChallans || [];

      const printWindow = window.open('', '', 'height=600,width=800');
      printWindow.document.write(`<html><head><title>${isReport ? 'Sales Challan Report' : 'Sales Challan List'}</title>`);
      printWindow.document.write('<style>');
      printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
      printWindow.document.write('h1 { text-align: center; color: #0b3a54; }');
      printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }');
      printWindow.document.write('th, td { border: 1px solid #ddd; padding: 5px; text-align: left; }');
      printWindow.document.write('th { background-color: #dff5ea; color: #0b3a54; font-weight: 600; }');
      printWindow.document.write('</style></head><body>');
      printWindow.document.write(`<h1>${isReport ? 'Sales Challan Report' : 'Sales Challan List'}</h1>`);
      printWindow.document.write('<table><thead><tr>');
      printWindow.document.write('<th>No.</th>');
      Object.entries(columnLabels).forEach(([key, label]) => {
        if (exportColumns[key]) printWindow.document.write(`<th>${label}</th>`);
      });
      printWindow.document.write('</tr></thead><tbody>');

      challansToPrint.forEach((r, index) => {
        printWindow.document.write('<tr>');
        printWindow.document.write(`<td>${index + 1}</td>`);
        Object.entries(columnLabels).forEach(([key, label]) => {
          if (exportColumns[key]) {
            if (key === 'invoiceDate') {
              printWindow.document.write(`<td>${formatDateDDMMYYYY(r.invoiceDate)}</td>`);
            } else {
              printWindow.document.write(`<td>${r[key] || ''}</td>`);
            }
          }
        });
        printWindow.document.write('</tr>');
      });
      printWindow.document.write('</tbody></table></body></html>');
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error("Print Error:", error);
      toast.error("Failed to print");
    } finally {
      setLoading(false);
    }
  };

  const deleteRow = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "Delete this challan?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await fetchApi(`/sales-challan/${id}`, { method: 'DELETE' });
          fetchChallans();
          Swal.fire('Deleted!', 'Sales challan has been deleted.', 'success');
        } catch (error) {
          toast.error("Failed to delete");
        }
      }
    });
  };



  const reportColumns = [
    { key: 'invoiceDate', label: 'SALE CHALLAN DATE', sortable: true, render: (r) => getInvoiceDateTime(r) },
    { key: 'paymentType', label: 'C/D', render: () => 'Debit' },
    { key: 'invoiceNo', label: 'SALE CHALLAN NO', sortable: true },
    { 
      key: 'customerTradeName', 
      label: 'PARTY NAME', 
      sortable: true, 
      render: (r, idx) => (
        <div
          className={`party-name-box ${idx === selectedIdx ? 'party-name-box--active' : ''}`}
          onClick={() => {
            setSelectedIdx(idx);
            handleCustomerClick(r);
          }}
        >
          {r.customerTradeName || r.customerName}
        </div>
      )
    },
    { key: 'customerCity', label: 'CITY', sortable: true, render: (r) => r.customerCity || r.customer_city || '' },
    { key: 'totalAmount', label: 'TOTAL AMOUNT', sortable: true, render: (r) => `${parseFloat(r.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/-` },
    { key: 'buyerTradeName', label: 'BILL BY', render: (r) => r.invoiceTypeName || r.invoiceCompanyName || r.invoice_company_name || '' }
  ];

  if (hasPermission('Sales Challan', 'view')) {
    reportColumns.push({
      key: 'preview',
      label: 'PREVIEW',
      render: (r) => (
        <button
          type="button"
          className="list-page-btn"
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            margin: 0,
            border: 'none',
            backgroundColor: '#0b3a54',
            color: 'white',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          onClick={() => onPreview && onPreview({ ...r, isChallan: true })}
        >
          Preview
        </button>
      )
    });
  }

  const standardColumns = [
    {
      key: 'customerTradeName',
      label: 'TRADE NAME',
      sortable: true,
      render: (r, idx) => (
        <div
          className={`party-name-box ${idx === selectedIdx ? 'party-name-box--active' : ''}`}
          onClick={() => {
            setSelectedIdx(idx);
            handleCustomerClick(r);
          }}
        >
          {r.customerTradeName}
        </div>
      )
    },
    { key: 'customerName', label: 'CUSTOMER NAME', sortable: true },
    { key: 'invoiceName', label: 'CHALLAN NAME' },
    { key: 'saleType', label: 'SALE TYPE' },
    { key: 'invoiceNo', label: 'CHALLAN NO', sortable: true },
    { key: 'invoiceDate', label: 'CHALLAN DATE', sortable: true, render: (r) => formatDateDDMMYYYY(r.invoiceDate) },
    { key: 'totalAmount', label: 'TOTAL', sortable: true }
  ];

  if (hasPermission('Sales Challan', 'view')) {
    standardColumns.push({
      key: 'print',
      label: 'PRINT',
      style: { textAlign: 'center' },
      render: (r) => (
        <button className="list-page-icon-btn" title="Print/Preview" onClick={() => onPreview && onPreview({ ...r, isChallan: true })}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
        </button>
      )
    });
  }

  if (!isReport && (hasPermission('Sales Challan', 'update') || hasPermission('Sales Challan', 'delete'))) {
    standardColumns.push({
      key: 'actions',
      label: 'ACTIONS',
      render: (r) => (
        <div className="list-page-action-icons">
          {hasPermission('Sales Challan', 'update') && (
            <button className="list-page-icon-btn" title="Edit" onClick={() => {
              if (onEditInvoice) {
                onEditInvoice(r);
              } else {
                setEditingRow(r);
                setShowAddForm(true);
              }
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </button>
          )}
          {hasPermission('Sales Invoice', 'add') && (
            <button className="list-page-icon-btn" title="Convert to Invoice" onClick={() => onConvert && onConvert(r)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><path d="M9 14l2 2 4-4"></path></svg>
            </button>
          )}
          {hasPermission('Sales Challan', 'delete') && (
            <button className="list-page-icon-btn" title="Delete" onClick={() => deleteRow(r.id)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
            </button>
          )}
        </div>
      )
    });
  }

  const columns = isReport ? reportColumns : standardColumns;

  const footerRow = sortedRows.length > 0 ? (
    isReport ? (
      <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
        <td colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
        <td style={{ fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
          {sortedRows.reduce((sum, r) => sum + (parseFloat(r.totalAmount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/-
        </td>
        <td></td>
        {hasPermission('Sales Challan', 'view') && <td></td>}
      </tr>
    ) : (
      <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
        <td colSpan={6} style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
        <td style={{ fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
          {sortedRows.reduce((sum, r) => sum + (parseFloat(r.totalAmount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </td>
        {hasPermission('Sales Challan', 'view') && <td></td>}
        {!isReport && (hasPermission('Sales Challan', 'update') || hasPermission('Sales Challan', 'delete')) && <td></td>}
      </tr>
    )
  ) : null;

  return (
    <div className="list-page-container">
      <div className="list-page-header">
        <h1 className="list-page-title">{isReport ? 'Sales Challan Report' : 'Sales Challan List'}</h1>
        <div className="list-page-action-buttons">
          {hasPermission('Sales Challan', 'report') && (
            <>
              <button className="list-page-btn list-page-btn-excel" onClick={() => { setExportType('excel'); setIsExportModalOpen(true); }}>EXCEL</button>
              <button className="list-page-btn list-page-btn-print" style={{ backgroundColor: '#ef4444' }} onClick={() => { setExportType('print'); setIsExportModalOpen(true); }}>PRINT</button>
            </>
          )}
          {!isReport && hasPermission('Sales Challan', 'add') && (
            <button className="list-page-btn list-page-btn-add" onClick={() => onAddInvoice ? onAddInvoice() : setShowAddForm(true)}>
              ADD SALES CHALLAN
            </button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={sortedRows}
        sortConfig={sortConfig}
        onSort={requestSort}
        loading={loading}
        emptyMessage="No records found"
        page={page}
        pageSize={pageSize}
        totalEntries={totalEntries}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        footerRow={footerRow}
        onRowClick={(r, idx) => {
          setSelectedIdx(idx);
        }}
        rowClassName={(r, idx) => idx === selectedIdx ? 'selected-row' : ''}
        searchBar={
          <>
            <SearchBar value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search challans..." />
            <div className="list-page-filter-box">
              <div className="list-page-filter-group">
                <label className="list-page-filter-label">Start Date</label>
                <AirDatePicker
                  className="list-page-filter-date"
                  value={draftStartDate}
                  onChange={setDraftStartDate}
                  placeholder="Start Date"
                />
              </div>
              <div className="list-page-filter-group">
                <label className="list-page-filter-label">End Date</label>
                <AirDatePicker
                  className="list-page-filter-date"
                  value={draftEndDate}
                  onChange={setDraftEndDate}
                  placeholder="End Date"
                />
              </div>
              <div className="list-page-filter-group">
                <label className="list-page-filter-label">Customer</label>
                <select
                  className="list-page-filter-select"
                  value={draftCustomer}
                  onChange={e => setDraftCustomer(e.target.value)}
                >
                  <option value="">---Select Customer---</option>
                  {customers
                    .filter(c => c.customerTradeName || c.customerName)
                    .sort((a, b) => (a.customerTradeName || a.customerName).localeCompare(b.customerTradeName || b.customerName))
                    .map(c => (
                      <option key={c.id || c._id} value={c.customerTradeName || c.customerName}>
                        {c.customerTradeName}{c.customerName ? ` (${c.customerName})` : ''}
                      </option>
                    ))}
                </select>
              </div>
              <button className="list-page-btn list-page-btn-submit" onClick={onSubmitFilters}>Submit</button>
              <button className="list-page-btn list-page-btn-reset" onClick={onResetFilters}>Reset</button>
            </div>
          </>
        }
      />
      {isExportModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', padding: '20px', borderRadius: '8px', width: '500px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h2 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: 'bold' }}>
                Select Columns to {exportType === 'excel' ? 'Export' : 'Print'}
              </h2>
              <button
                onClick={() => {
                  const allKeys = Object.keys(columnLabels);
                  const allChecked = allKeys.every(k => exportColumns[k]);
                  const newExportColumns = { ...exportColumns };
                  allKeys.forEach(k => newExportColumns[k] = !allChecked);
                  setExportColumns(newExportColumns);
                }}
                style={{
                  padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #cbd5e1',
                  backgroundColor: '#f8fafc', cursor: 'pointer'
                }}
              >
                {Object.keys(columnLabels).every(k => exportColumns[k]) ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
              {Object.entries(columnLabels).map(([key, label]) => {
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333', cursor: 'pointer', padding: '4px' }}>
                    <input
                      type="checkbox"
                      checked={exportColumns[key]}
                      onChange={() => toggleExportColumn(key)}
                      style={{ accentColor: '#06b6d4', width: '16px', height: '16px' }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', pt: '10px', borderTop: '1px solid #e2e8f0', marginTop: '10px', paddingTop: '15px' }}>
              <button
                onClick={() => setIsExportModalOpen(false)}
                style={{
                  padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd5e1',
                  backgroundColor: 'white', color: '#475569', cursor: 'pointer', fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (exportType === 'excel') {
                    handleExcelDownload();
                  } else {
                    handlePrint();
                  }
                  setIsExportModalOpen(false);
                }}
                style={{
                  padding: '8px 16px', borderRadius: '4px', border: 'none',
                  backgroundColor: '#06b6d4', color: 'white', cursor: 'pointer', fontWeight: '600'
                }}
              >
                {exportType === 'excel' ? 'Export Excel' : 'Print'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* COMPREHENSIVE HISTORY SECTION - Shows only Converted challans */}
      <div style={{ marginTop: '50px', borderTop: '3px solid #1e3a5f', paddingTop: '30px', paddingBottom: '50px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', color: '#1e3a5f', fontWeight: 'bold', margin: 0 }}>COMPREHENSIVE HISTORY</h2>
          <span style={{ fontSize: '13px', color: '#64748b' }}>Showing last 20 converted challans</span>
        </div>
        <div style={{ boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', borderRadius: '8px', overflow: 'hidden' }}>
          <DataTable
            columns={[
              { key: 'challan_no', label: 'CHALLAN NO', render: h => h.challan_no || h.invoiceNo },
              { key: 'date', label: 'DATE', render: h => formatDateDDMMYYYY(h.challan_date || h.invoiceDate) },
              { key: 'customer_name', label: 'CUSTOMER NAME', render: h => h.customer_name || h.customerName },
              { key: 'sale_type', label: 'SALE TYPE', render: h => h.saleType || h.sale_type_name },
              {
                key: 'total_amount',
                label: 'TOTAL AMOUNT',
                style: { textAlign: 'right', fontWeight: '600', color: '#059669' },
                render: h => `₹${parseFloat(h.total_amount || h.totalAmount || 0).toFixed(2)}`
              }
            ]}
            data={history}
            loading={false}
            emptyMessage="No history found"
            page={1}
            pageSize={history.length > 0 ? history.length : 10}
            totalEntries={history.length}
            totalPages={1}
            onPageChange={() => {}}
            onPageSizeChange={() => {}}
            footerRow={
              history.length > 0 ? (
                <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
                  <td colSpan="4" style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                    ₹{history.reduce((sum, h) => sum + (parseFloat(h.total_amount || h.totalAmount || 0)), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ) : null
            }
          />
        </div>
      </div>
    </div>
  );
};

export default SalesChallanList;
